import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Blur the currently focused element to reset button states
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center min-h-screen">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm min-h-screen"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative dark:bg-surface bg-light-surface rounded-lg shadow-2xl border dark:border-border-color border-light-border-color w-full max-w-2xl mx-2 sm:mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b dark:border-border-color border-light-border-color">
          <h2 className="text-lg sm:text-xl font-semibold dark:text-text-primary text-light-text-primary pr-2">
            {title || 'Details'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-72px)] sm:max-h-[calc(90vh-88px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
